import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  Sparkles,
  ShieldCheck,
  Handshake,
  ArrowRight,
  FileText,
  Users2,
  ArrowUpRight,
  Radio,
} from "lucide-react";
import { PrimaryCTA, SecondaryCTA } from "@/components/CTAButtons";
import LiveFeed from "@/components/LiveFeed";
import {
  Reveal,
  Stagger,
  StaggerItem,
  MaskedLines,
  Parallax,
  Marquee,
} from "@/components/Motion";

const IMG = {
  spoons:
    "https://images.unsplash.com/photo-1509358271058-acd22cc93898?w=1400&q=80&auto=format&fit=crop",
  flatlay:
    "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=1400&q=80&auto=format&fit=crop",
  powder:
    "https://images.unsplash.com/photo-1610725664285-7c57e6eeac3f?w=1200&q=80&auto=format&fit=crop",
  cherries:
    "https://images.unsplash.com/photo-1439127989242-c3749a012eac?w=1200&q=80&auto=format&fit=crop",
};

const CHAPTERS = [
  {
    n: "01",
    icon: Sparkles,
    kicker: "The premise",
    title: "Curated intelligence, never raw data.",
    body:
      "We do not sell access to a database. Every recommendation is authored by analysts who read the specialty-ingredients market daily — filtering noise into a decision you can defend to leadership.",
    image: IMG.flatlay,
    caption: "FIG. 01 — Category signal, hand-sorted",
  },
  {
    n: "02",
    icon: ShieldCheck,
    kicker: "The rigour",
    title: "Verification before recommendation.",
    body:
      "Counterparties are vetted through conference relationships, on-the-ground intelligence, and FFIB's cross-border network — long before a name ever reaches your desk.",
    image: IMG.powder,
    caption: "FIG. 02 — Provenance, verified at source",
  },
  {
    n: "03",
    icon: Handshake,
    kicker: "The execution",
    title: "Warm introductions, seen through.",
    body:
      "When the fit is right, FFIB hosts the introduction and stays in the room — through negotiation, sampling, and first delivery. Intelligence that ends in a handshake, not a hyperlink.",
    image: IMG.cherries,
    caption: "FIG. 03 — Relationship, brought to the table",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Share your need",
    body:
      "A short intake captures your ingredient, geography, volume, and timeline — no logins, no dashboards.",
  },
  {
    n: "02",
    title: "FFIB analyzes internally",
    body:
      "Our team weighs the opportunity against a private base of counterparties and live market signals.",
  },
  {
    n: "03",
    title: "Intelligence is produced",
    body:
      "A tailored shortlist, opportunity brief, or sourcing report — authored for your specific decision.",
  },
  {
    n: "04",
    title: "Introductions managed",
    body:
      "You receive a written deliverable. Where useful, FFIB hosts the warm introduction and supports follow-through.",
  },
];

export default function Home() {
  const reduce = useReducedMotion();

  return (
    <div data-testid="home-page">
      {/* ============ KINETIC HERO ============ */}
      <section className="relative overflow-hidden border-b border-brand-border">
        {/* backdrop */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(47,61,30,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(47,61,30,0.5) 1px, transparent 1px)",
              backgroundSize: "76px 76px",
              maskImage:
                "radial-gradient(ellipse at 20% 0%, black 35%, transparent 78%)",
              WebkitMaskImage:
                "radial-gradient(ellipse at 20% 0%, black 35%, transparent 78%)",
            }}
          />
          <div
            aria-hidden="true"
            className="absolute -top-40 right-[-10%] w-[820px] h-[820px] rounded-full"
            style={{
              background:
                "radial-gradient(circle at center, rgba(122,142,74,0.16), transparent 62%)",
              filter: "blur(30px)",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            {/* Left — copy */}
            <div className="lg:col-span-7">
              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="inline-flex items-center gap-2.5 rounded-full border border-brand-olive/30 bg-brand-accent/10 pl-3 pr-4 py-1.5"
                data-testid="home-hero-eyebrow"
              >
                <Radio className="w-3.5 h-3.5 text-brand-olive" strokeWidth={1.8} />
                <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-brand-olive">
                  Living Sourcing Intelligence
                </span>
              </motion.div>

              <h1 className="font-display mt-8 text-[2.7rem] leading-[1.0] sm:text-6xl lg:text-[4.7rem] lg:leading-[0.96] text-brand-deep font-medium tracking-[-0.03em]">
                <MaskedLines
                  lines={["We run the", "sourcing engine.", "You get the"]}
                  className="block"
                />
                <span className="block overflow-hidden pb-[0.08em]">
                  <motion.span
                    className="block text-brand-olive"
                    initial={reduce ? { y: "0%", opacity: 0 } : { y: "115%" }}
                    animate={reduce ? { y: "0%", opacity: 1 } : { y: "0%" }}
                    transition={{
                      duration: 0.95,
                      delay: 0.15 + 3 * 0.11,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    deliverables.
                  </motion.span>
                </span>
              </h1>

              <motion.p
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
                className="mt-8 max-w-xl text-brand-warm text-lg leading-relaxed font-light"
              >
                Buyers receive curated supplier shortlists and sourcing
                intelligence. Suppliers receive relationship-led representation
                — never a public listing. The platform is FFIB's. The outcomes
                are yours.
              </motion.p>

              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
                className="mt-10 flex flex-wrap gap-3"
              >
                <PrimaryCTA to="/get-started" testId="home-hero-get-started">
                  Start a Conversation
                </PrimaryCTA>
                <SecondaryCTA to="/platform" testId="home-hero-explore-platform">
                  See How It Works
                </SecondaryCTA>
              </motion.div>
            </div>

            {/* Right — dynamic Live Intelligence Feed as the hero visual */}
            <div className="lg:col-span-5">
              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 24 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                {/* spotlight glow */}
                <div
                  aria-hidden="true"
                  className="absolute -inset-8 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at 60% 40%, rgba(122,142,74,0.28), transparent 60%)",
                    filter: "blur(28px)",
                  }}
                />
                <div className="relative">
                  <LiveFeed size="hero" />
                  {/* FFIB mark below the card */}
                  <div className="mt-4 flex items-center justify-between px-1">
                    <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-brand-quiet">
                      FIG. 00 — Sourcing Signals
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-brand-quiet">
                      FFIB
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ EDITORIAL MARQUEE ============ */}
      <section className="border-b border-brand-border bg-brand-ivory py-6 overflow-hidden">
        <Marquee>
          {[
            "Nutraceuticals",
            "Food & Beverage",
            "Animal Feed",
            "Functional Ingredients",
            "Botanical Extracts",
            "Proteins & Peptides",
          ].map((w, i) => (
            <span key={i} className="flex items-center">
              <span className="font-display text-2xl md:text-3xl font-light text-brand-deep px-8">
                {w}
              </span>
              <span className="text-brand-olive text-xl">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* ============ MANIFESTO CHAPTERS ============ */}
      <section className="py-24 lg:py-36 mt-10 sm:mt-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-brand-olive">
                The Manifesto
              </span>
              <span className="h-px flex-1 bg-brand-border" />
            </div>
            <h2 className="font-display mt-8 max-w-3xl text-3xl sm:text-4xl lg:text-5xl text-brand-deep font-light leading-[1.08]">
              A premium intelligence layer — not a marketplace.
            </h2>
          </Reveal>

          <div className="mt-20 space-y-24 lg:space-y-32">
            {CHAPTERS.map((c, i) => {
              const flip = i % 2 === 1;
              const Icon = c.icon;
              return (
                <div
                  key={c.n}
                  data-testid={`home-chapter-${c.n}`}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
                >
                  {/* text */}
                  <Reveal
                    className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}
                    y={36}
                  >
                    <div className="flex items-baseline gap-5">
                      <span className="font-display text-6xl lg:text-7xl font-light text-brand-olive/25 leading-none tabular-nums">
                        {c.n}
                      </span>
                      <div className="pt-2">
                        <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.24em] uppercase text-brand-olive">
                          <Icon className="w-4 h-4" strokeWidth={1.6} />
                          {c.kicker}
                        </div>
                      </div>
                    </div>
                    <h3 className="font-display mt-7 text-3xl lg:text-[2.6rem] lg:leading-[1.05] text-brand-deep font-light tracking-[-0.02em]">
                      {c.title}
                    </h3>
                    <p className="mt-6 max-w-lg text-brand-warm text-lg leading-relaxed font-light">
                      {c.body}
                    </p>
                  </Reveal>

                  {/* framed image with parallax */}
                  <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                    <Reveal y={44} duration={0.9}>
                      <div className="relative">
                        <div
                          aria-hidden="true"
                          className={`absolute -inset-6 rounded-full ${
                            flip ? "left-0" : "right-0"
                          }`}
                          style={{
                            background:
                              "radial-gradient(circle at center, rgba(122,142,74,0.18), transparent 62%)",
                            filter: "blur(26px)",
                          }}
                        />
                        <div className="relative overflow-hidden rounded-[1.3rem] border border-brand-olive/20 shadow-[0_36px_80px_-34px_rgba(47,61,30,0.5)]">
                          <Parallax offset={36}>
                            <img
                              src={c.image}
                              alt={c.caption}
                              loading="lazy"
                              className="w-full h-[360px] lg:h-[460px] object-cover scale-110"
                            />
                          </Parallax>
                          <div
                            aria-hidden="true"
                            className="absolute inset-0"
                            style={{
                              background:
                                "linear-gradient(to top, rgba(20,24,12,0.5), transparent 45%)",
                            }}
                          />
                          <span className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.24em] uppercase text-brand-cream/90">
                            {c.caption}
                          </span>
                        </div>
                      </div>
                    </Reveal>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ LIVE FEED (contextual) ============ */}
      <section className="py-24 lg:py-32 bg-brand-ivory border-y border-brand-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-brand-olive">
                Boots on the ground
              </span>
              <h2 className="font-display mt-6 text-3xl sm:text-4xl lg:text-5xl text-brand-deep font-light leading-[1.08]">
                Intelligence gathered where the industry actually meets.
              </h2>
              <p className="mt-6 max-w-lg text-brand-warm text-lg leading-relaxed font-light">
                We invest in being present at the conferences and trade halls
                that shape specialty ingredients — then translate what we learn
                into signals our analysts can act on for you.
              </p>
              <div className="mt-9">
                <SecondaryCTA to="/platform" testId="home-livefeed-cta">
                  Inside the platform
                </SecondaryCTA>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal y={40} delay={0.1}>
              <LiveFeed />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ WHO WE SERVE ============ */}
      <section className="py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-brand-olive">
                Who We Serve
              </span>
              <span className="h-px flex-1 bg-brand-border" />
            </div>
            <h2 className="font-display mt-8 max-w-3xl text-3xl sm:text-4xl lg:text-5xl text-brand-deep font-light leading-[1.08]">
              Built for both sides of the specialty ingredients trade.
            </h2>
          </Reveal>

          <Stagger className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[
              {
                role: "Buyers",
                title: "Sourcing teams who need answers, not access.",
                points: [
                  "Procurement consolidation across categories",
                  "Alternative supplier discovery & risk reduction",
                  "Cross-border validation handled for you",
                ],
                to: "/for-buyers",
                cta: "Explore For Buyers",
                Icon: FileText,
              },
              {
                role: "Suppliers",
                title: "Qualified suppliers seeking targeted buyer access.",
                points: [
                  "Relationship-led market entry",
                  "Differentiated positioning in noisy categories",
                  "Warm buyer introductions, not cold outreach",
                ],
                to: "/for-suppliers",
                cta: "Explore For Suppliers",
                Icon: Users2,
              },
            ].map((c) => (
              <StaggerItem key={c.role}>
                <div
                  data-testid={`home-audience-${c.role.toLowerCase()}`}
                  className="group relative h-full bg-brand-ivory border border-brand-border hover:border-brand-olive/40 transition-colors duration-500 p-10 md:p-14 rounded-2xl"
                >
                  <div className="flex items-center gap-3 text-[11px] font-semibold tracking-[0.24em] uppercase text-brand-olive">
                    <c.Icon className="w-4 h-4" strokeWidth={1.5} />
                    {c.role}
                  </div>
                  <h3 className="font-display mt-8 text-2xl md:text-3xl text-brand-deep font-light leading-snug">
                    {c.title}
                  </h3>
                  <ul className="mt-10 space-y-3.5">
                    {c.points.map((pt) => (
                      <li
                        key={pt}
                        className="flex items-start gap-3 text-brand-warm font-light"
                      >
                        <span className="mt-2.5 w-1.5 h-1.5 bg-brand-olive flex-shrink-0" />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={c.to}
                    data-testid={`home-audience-cta-${c.role.toLowerCase()}`}
                    className="mt-12 inline-flex items-center gap-2 text-sm text-brand-deep border-b border-brand-olive/40 pb-1 hover:border-brand-olive hover:text-brand-olive transition-colors"
                  >
                    {c.cta}
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ============ PROCESS ============ */}
      <section className="py-24 lg:py-36 bg-brand-ivory border-y border-brand-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-brand-olive">
                How It Works
              </span>
              <span className="h-px flex-1 bg-brand-border" />
            </div>
            <h2 className="font-display mt-8 max-w-3xl text-3xl sm:text-4xl lg:text-5xl text-brand-deep font-light leading-[1.08]">
              Four steps from need to executed introduction.
            </h2>
          </Reveal>

          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-border rounded-2xl overflow-hidden">
            {PROCESS.map((step) => (
              <StaggerItem key={step.n}>
                <div
                  data-testid={`home-process-step-${step.n}`}
                  className="h-full bg-brand-cream p-10 lg:p-12"
                >
                  <div className="font-mono text-xs text-brand-olive tracking-[0.2em]">
                    STEP {step.n}
                  </div>
                  <h3 className="font-display mt-8 text-xl text-brand-deep font-medium leading-snug">
                    {step.title}
                  </h3>
                  <p className="mt-5 text-sm text-brand-muted leading-relaxed font-light">
                    {step.body}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ============ CLOSING CTA ============ */}
      <section className="relative py-28 lg:py-40 overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(47,61,30,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(47,61,30,0.5) 1px, transparent 1px)",
            backgroundSize: "76px 76px",
            maskImage:
              "radial-gradient(ellipse at 80% 100%, black 30%, transparent 72%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at 80% 100%, black 30%, transparent 72%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-brand-olive">
              Begin
            </div>
          </Reveal>
          <h2 className="font-display mt-7 text-4xl sm:text-5xl lg:text-[4rem] text-brand-deep font-medium leading-[1.02] tracking-[-0.02em]">
            <MaskedLines
              animateOnView
              lines={["Tell us what you're sourcing.", "We'll do the analysis."]}
              stagger={0.12}
              delay={0}
            />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-8 text-brand-warm max-w-2xl text-lg leading-relaxed font-light">
              Share your ingredient, geography, and timeline. FFIB analysts
              return curated intelligence — and host the introductions where the
              fit is right.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-5">
              <PrimaryCTA to="/get-started" testId="home-footer-cta-get-started">
                Start a Conversation
              </PrimaryCTA>
              <Link
                to="/case-studies"
                data-testid="home-footer-cta-case-studies"
                className="inline-flex items-center gap-2 text-sm text-brand-warm hover:text-brand-deep transition-colors"
              >
                Read case studies
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
